/**
 * Export utilities for poll results
 * Supports CSV, JSON, and Excel (multi-sheet) formats with styling via xlsx-js-style
 */
import * as XLSX from 'xlsx-js-style';
import { formatDateTime } from './formatDate';

/** Get display question text from activity content (for export context) */
function getExportQuestion(activity) {
    const content = activity?.content;
    if (!content) return '';
    if (content.question != null && String(content.question).trim() !== '') {
        return String(content.question).trim();
    }
    if (activity?.type === 'quiz' && Array.isArray(content.questions) && content.questions.length > 0) {
        return content.questions.map((q, i) => `Q${i + 1}: ${(q.text || '').trim() || '(No text)'}`).join(' | ');
    }
    return '';
}

/** Get description text from activity content */
function getExportDescription(activity) {
    const content = activity?.content;
    if (!content?.settings) return '';
    if (content.settings.description && content.settings.descText != null) {
        return String(content.settings.descText).trim();
    }
    return '';
}

/**
 * Export results as CSV
 * @param {Array} responses - Response objects
 * @param {Object} activity - Activity details with content/options
 * @param {String} filename - Output filename
 */
export const exportToCSV = (responses, activity, filename = 'results.csv') => {
    if (!responses || responses.length === 0) {
        alert('No responses to export');
        return;
    }

    try {
        const question = getExportQuestion(activity);
        const description = getExportDescription(activity);

        // Prepare CSV headers: Question & Description first so it's clear which poll the answers belong to
        const headers = ['Question', 'Description', 'Participant', 'Answer', 'Timestamp', 'Activity Type'];

        // Prepare CSV rows (same question/description for each row of this activity)
        const rows = responses.map(r => [
            question,
            description,
            r.participant || 'Anonymous',
            formatAnswerForCSV(r.answer, activity),
            formatDateTime(r.created_at),
            activity?.type || 'Unknown'
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...rows.map(row => 
                row.map(cell => {
                    // Escape cells with commas or quotes
                    const str = String(cell || '').replace(/"/g, '""');
                    return `"${str}"`;
                }).join(',')
            )
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        downloadBlob(blob, filename);
    } catch (err) {
        console.error('CSV export failed:', err);
        alert('Failed to export CSV');
    }
};

/**
 * Export results as JSON
 * @param {Array} responses - Response objects
 * @param {Object} activity - Activity details
 * @param {String} filename - Output filename
 */
export const exportToJSON = (responses, activity, filename = 'results.json') => {
    if (!responses || responses.length === 0) {
        alert('No responses to export');
        return;
    }

    try {
        const question = getExportQuestion(activity);
        const description = getExportDescription(activity);

        const data = {
            activity: {
                id: activity?.id,
                title: activity?.title,
                type: activity?.type,
                question: question || undefined,
                description: description || undefined,
                exportedAt: new Date().toISOString(),
            },
            summary: {
                totalResponses: responses.length,
                dateRange: {
                    earliest: responses.length > 0 ? responses[responses.length - 1].created_at : null,
                    latest: responses.length > 0 ? responses[0].created_at : null,
                }
            },
            responses: responses.map(r => ({
                participant: r.participant || 'Anonymous',
                answer: r.answer,
                timestamp: r.created_at,
            }))
        };

        const jsonContent = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
        downloadBlob(blob, filename);
    } catch (err) {
        console.error('JSON export failed:', err);
        alert('Failed to export JSON');
    }
};

/**
 * Helper: Format answer for CSV display
 * Takes activity context to convert indices to actual option text
 */
function formatAnswerForCSV(answer, activity) {
    if (!answer) return '';
    
    const type = activity?.type;
    const content = activity?.content;
    
    // Handle string answers directly
    if (typeof answer === 'string') {
        return answer;
    }
    
    // Handle numeric answers directly
    if (typeof answer === 'number') {
        return String(answer);
    }
    
    // Handle arrays of numbers/strings
    if (Array.isArray(answer)) {
        return answer.join('; ');
    }
    
    // Handle object answers based on activity type
    if (typeof answer === 'object') {
        // Multiple Choice: {"selected": 3} or {"selected": [1, 3]}
        if (type === 'multiple-choice' && answer.selected !== undefined) {
            const options = content?.options || [];
            const selectedIndices = Array.isArray(answer.selected) ? answer.selected : [answer.selected];
            const selectedTexts = selectedIndices
                .map(idx => options[idx] || `Option ${idx + 1}`)
                .filter(Boolean);
            return selectedTexts.join('; ') || JSON.stringify(answer);
        }
        
        // Quiz: {"selected": 2} or {"correct": true, "answer": "text"}
        if (type === 'quiz') {
            const options = content?.options || [];
            if (answer.selected !== undefined) {
                const optionText = options[answer.selected] || `Option ${answer.selected + 1}`;
                return optionText;
            }
            if (answer.answer !== undefined) {
                return String(answer.answer);
            }
        }
        
        // Rating: {"rating": 5}
        if (type === 'rating' && answer.rating !== undefined) {
            return String(answer.rating);
        }

        // Rating Scale: same as rating, single numeric value
        if (type === 'rating-scale' && answer.rating !== undefined) {
            return String(answer.rating);
        }
        
        // Ranking: {"order": [1, 0, 2]} indices or legacy {"ranking": ["B", "A", "C"]} strings
        if (type === 'ranking') {
            const items = content?.items || content?.options || [];
            const order = answer.order;
            const ranking = answer.ranking;
            if (Array.isArray(order) && order.length) {
                const orderedItems = order.map(idx => items[idx] || `Item ${idx + 1}`).filter(Boolean);
                return orderedItems.join(' → ') || JSON.stringify(answer);
            }
            if (Array.isArray(ranking) && ranking.length) return ranking.join(' → ');
        }
        
        // Word Cloud: {"text": "hello"}
        if (type === 'word-cloud' && answer.text !== undefined) {
            return String(answer.text);
        }
        
        // Open Text: {"text": "..."} single or {"texts": ["...", "..."]} multiple
        if (type === 'open-text' || type === 'audience-qa') {
            if (Array.isArray(answer.texts) && answer.texts.length > 0) {
                return answer.texts.map(t => String(t)).join('; ');
            }
            if (answer.text !== undefined) return String(answer.text);
        }
        
        // Correct Order: order is array of item strings (participant's order) or legacy indices
        if (type === 'correct-order' && answer.order && Array.isArray(answer.order)) {
            const items = content?.items || [];
            const raw = answer.order;
            const isIndices = raw.length > 0 && typeof raw[0] === 'number';
            const userOrder = isIndices
                ? raw.map(idx => items[idx] || `Item ${idx + 1}`).filter(Boolean)
                : raw.map(s => String(s)).filter(Boolean);
            return userOrder.join(' → ') || JSON.stringify(answer);
        }
        
        // Spinner Wheel: {"selected": "Option 1", "index": 0} or legacy spinResult
        if (type === 'spinner-wheel') {
            if (answer.selected !== undefined && answer.selected !== null && answer.selected !== '') return String(answer.selected);
            if (answer.spinResult !== undefined && answer.spinResult !== null && answer.spinResult !== '') return String(answer.spinResult);
        }
        
        // Categories: {"assignments": { "Item1": "Cat A", "Item2": "Cat B" }}
        if (type === 'categories' && answer.assignments && typeof answer.assignments === 'object') {
            const a = answer.assignments;
            const byCat = {};
            Object.entries(a).forEach(([item, cat]) => {
                const c = String(cat || '');
                if (!byCat[c]) byCat[c] = [];
                byCat[c].push(String(item));
            });
            return Object.entries(byCat).map(([cat, items]) => `${cat}: ${items.join(', ')}`).join('; ') || JSON.stringify(a);
        }
        
        // Fallback: return first non-empty value or stringify
        const firstValue = Object.values(answer).find(v => v !== undefined && v !== null && v !== '');
        if (firstValue !== undefined) {
            if (Array.isArray(firstValue)) {
                return firstValue.join('; ');
            }
            return String(firstValue);
        }
        
        return JSON.stringify(answer);
    }
    
    return String(answer || '');
}

/**
 * Helper: Trigger browser download
 */
function downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/**
 * Get appropriate filename with timestamp
 */
export const getExportFilename = (activityTitle, format) => {
    const date = new Date().toISOString().split('T')[0];
    const title = (activityTitle || 'results')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 30);
    return `${title}_${date}.${format}`;
};

/** Excel sheet names: max 31 chars, no \ / ? * [ ] */
function sanitizeSheetName(title) {
    if (!title || typeof title !== 'string') return 'Sheet';
    let name = title.replace(/[\\/?*[\]]/g, ' ').trim().slice(0, 31);
    return name || 'Sheet';
}

/** ARGB hex for Excel (alpha FF + RRGGBB) */
const EXCEL_COLORS = {
    sectionHeaderBg: 'FF4472C4',
    sectionHeaderFont: 'FFFFFFFF',
    metaLabelBg: 'FFF2F2F2',
    tableHeaderBg: 'FFD6DCE4',
    tableHeaderFont: 'FF2F5496',
    dataZebraBg: 'FFF5F5F5',
};

/**
 * Apply professional background and font styles to worksheet.
 * @param {object} worksheet - XLSX worksheet
 * @param {Array[]} sheetData - full sheet data to find table header row
 * @param {number} numCols - number of columns in table
 */
function applyWorksheetStyles(worksheet, sheetData, numCols) {
    const headerRowIndex = sheetData.findIndex((row) => Array.isArray(row) && row[0] === 'Participant');
    if (headerRowIndex < 0) return;
    const metaEnd = headerRowIndex;
    const totalRows = sheetData.length;

    for (let r = 0; r < totalRows; r++) {
        for (let c = 0; c < numCols; c++) {
            const ref = XLSX.utils.encode_cell({ r, c });
            const cell = worksheet[ref];
            if (!cell) continue;
            if (!cell.s) cell.s = {};
            if (r === 0) {
                cell.s.fill = { patternType: 'solid', fgColor: { rgb: EXCEL_COLORS.sectionHeaderBg } };
                cell.s.font = { color: { rgb: EXCEL_COLORS.sectionHeaderFont }, bold: true };
            } else if (r < metaEnd && c === 0) {
                cell.s.fill = { patternType: 'solid', fgColor: { rgb: EXCEL_COLORS.metaLabelBg } };
            } else if (r === headerRowIndex) {
                cell.s.fill = { patternType: 'solid', fgColor: { rgb: EXCEL_COLORS.tableHeaderBg } };
                cell.s.font = { color: { rgb: EXCEL_COLORS.tableHeaderFont }, bold: true };
            } else if (r > headerRowIndex && (r - headerRowIndex - 1) % 2 === 1) {
                cell.s.fill = { patternType: 'solid', fgColor: { rgb: EXCEL_COLORS.dataZebraBg } };
            }
        }
    }
}

/**
 * Get option text for a single quiz question from response answer.
 * answer.answers = { 0: selectedIdx, 1: selectedIdx, ... } (keys may be string or number)
 */
function getQuizQuestionAnswerText(activity, responseAnswer, questionIndex) {
    const questions = activity?.content?.questions || [];
    const q = questions[questionIndex];
    if (!q || !q.options || !Array.isArray(q.options)) return '';
    const answers = responseAnswer?.answers;
    if (answers == null || typeof answers !== 'object') return '';
    const selectedIdx = answers[questionIndex] ?? answers[String(questionIndex)];
    if (selectedIdx == null) return '—';
    const text = q.options[Number(selectedIdx)];
    return text != null ? String(text).trim() || '—' : '—';
}

/**
 * Compute quiz score: number of questions where participant's answer matches correct option index.
 */
function getQuizScore(activity, responseAnswer) {
    const questions = activity?.content?.questions || [];
    const answers = responseAnswer?.answers;
    if (answers == null || typeof answers !== 'object') return 0;
    return questions.reduce((acc, q, qi) => {
        const selected = answers[qi] ?? answers[String(qi)];
        return acc + (Number(selected) === Number(q.correct) ? 1 : 0);
    }, 0);
}

/**
 * Build sheet data for Quiz: metadata (event + each question text), then table Participant | Q1 | Q2 | ... | Score | Timestamp.
 */
function buildQuizSheetData(event, activity, responses) {
    const questions = activity?.content?.questions || [];
    const eventCode = event.code || '';
    const eventName = event.name || '';
    const activityTitle = activity?.title || 'Untitled';
    const activityDescription = getExportDescription(activity);

    const metaRows = [
        ['Event & poll details', ''],
        ['Event Code', eventCode],
        ['Event Name', eventName],
        ['Activity Title', activityTitle],
        ['Activity Type', 'quiz'],
    ];
    if (activityDescription) metaRows.push(['Description', activityDescription]);
    metaRows.push(['', '']);
    metaRows.push(['Quiz questions (each has multiple responses below)', '']);
    questions.forEach((q, qi) => {
        const qText = (q.text || '').trim() || `Question ${qi + 1}`;
        metaRows.push([`Question ${qi + 1}`, qText]);
        if (q.description && String(q.description).trim()) {
            metaRows.push([`  Description`, String(q.description).trim()]);
        }
    });
    metaRows.push(['', '']);

    const qHeaders = questions.map((_, qi) => `Q${qi + 1}`);
    const tableHeaders = ['Participant', ...qHeaders, 'Score', 'Timestamp'];
    const tableRows = responses.map((r) => {
        const ans = r.answer && typeof r.answer === 'object' ? r.answer : {};
        const participant = r.participant || 'Anonymous';
        const qCols = questions.map((_, qi) => getQuizQuestionAnswerText(activity, ans, qi));
        const score = getQuizScore(activity, ans);
        const total = questions.length;
        const timestamp = formatDateTime(r.created_at);
        return [participant, ...qCols, `${score}/${total}`, timestamp];
    });
    return [...metaRows, tableHeaders, ...tableRows];
}

/**
 * Export full event as one Excel file (.xlsx) with one sheet per poll.
 * Non-quiz: event/poll details at top, then table (Participant, Answer, Timestamp).
 * Quiz: event/poll details + each question text at top, then table (Participant, Q1, Q2, ..., Score, Timestamp).
 */
export const exportEventToExcel = (payload, filename = 'event-export.xlsx') => {
    if (!payload || !payload.event || !Array.isArray(payload.activities)) {
        return;
    }
    const { event, activities } = payload;
    const eventCode = event.code || '';
    const eventName = event.name || '';
    const workbook = XLSX.utils.book_new();
    const usedSheetNames = new Set();

    for (const { activity, responses } of activities) {
        if (!responses || responses.length === 0) continue;
        const question = getExportQuestion(activity);
        const description = getExportDescription(activity);
        const activityTitle = activity?.title || 'Untitled';
        const activityType = activity?.type || '';

        let sheetData;
        if (activityType === 'quiz') {
            sheetData = buildQuizSheetData(event, activity, responses);
        } else {
            // Section 1: Event & poll details (once at top) — Label | Value
            const metaRows = [
                ['Event & poll details', ''],
                ['Event Code', eventCode],
                ['Event Name', eventName],
                ['Activity Title', activityTitle],
                ['Activity Type', activityType],
            ];
            if (question) metaRows.push(['Question', question]);
            if (description) metaRows.push(['Description', description]);
            metaRows.push(['', '']);

            const tableHeaders = ['Participant', 'Answer', 'Timestamp'];
            const tableRows = responses.map((r) => [
                r.participant || 'Anonymous',
                formatAnswerForCSV(r.answer, activity),
                formatDateTime(r.created_at),
            ]);
            sheetData = [...metaRows, tableHeaders, ...tableRows];
        }

        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

        const numCols = Math.max(...sheetData.map((row) => (row || []).length), 1);
        const colWidths = Array.from({ length: numCols }, (_, i) => {
            if (i === 0) return { wch: 20 };
            if (i === 1 && activityType !== 'quiz') return { wch: 50 };
            if (activityType === 'quiz' && i === numCols - 1) return { wch: 22 };
            if (activityType === 'quiz' && i === numCols - 2) return { wch: 10 };
            return { wch: 28 };
        });
        worksheet['!cols'] = colWidths;

        applyWorksheetStyles(worksheet, sheetData, numCols);

        let sheetName = sanitizeSheetName(activityTitle);
        if (usedSheetNames.has(sheetName)) {
            let n = 1;
            while (usedSheetNames.has(`${sheetName.slice(0, 28)}${n}`)) n++;
            sheetName = `${sheetName.slice(0, 28)}${n}`;
        }
        usedSheetNames.add(sheetName);

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    }

    if (workbook.SheetNames.length === 0) return;
    XLSX.writeFile(workbook, filename);
};
